import { Component, ElementRef, HostListener, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoaderService } from '@app/main/cablepro/loader.service';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { TrialBannerComponent } from '@app/shared/layout/trial-banner/trial-banner.component';
import { CommonModule } from '@node_modules/@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@node_modules/@angular/router';
import { NgbModal, NgbModalConfig } from '@node_modules/@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@node_modules/@ng-select/ng-select';
import { Subscription } from '@node_modules/rxjs';
import { filter } from '@node_modules/rxjs/operators';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase, RequireLogin } from '@shared/common/app-component-base';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { UserLoginInfoDto } from '@shared/service-proxies/service-proxies';
import { SharedService } from '@shared/service-proxies/shared.service';
import { FeatureCode } from '@shared/utils/feature-code';
import { LoginService } from 'account/login/login.service';
import { WebfacingLoginComponent } from 'webfacing/webfacing-login/webfacing-login.component';
import { WebfacingRegisterComponent } from 'webfacing/webfacing-register/webfacing-register.component';
import { RegionalStandardModalComponent } from 'webfacing/regional-standard-modal/regional-standard-modal.component';
import { RegionalStandardService } from 'webfacing/services/regional-standard.service';
import { RegionalDetectionResult, StandardCode, STANDARD_DISPLAY_NAME } from 'webfacing/services/regional-standard.model';

/**
 * Interface for calculator navigation items
 */
export interface CalculatorNavItem {
  name: string;
  sortName: string;
  url?: string;
  external?: boolean;
  disabled?: boolean;
  icon?: string;
}

/**
 * Interface for standard variants (Cable Sizing variants)
 */
export interface StandardVariant {
  name: string;
  code: string;
  url: string;
  label: string;
}

/**
 * Interface for tab data
 */
export interface CalculatorTab {
  id: string;
  label: string;
  icon: string;
  url: string;
  isActive: boolean;
  svgPath?: string;
}

export interface PromoBannerConfig {
  barBackgroundColour: string;
  barTextColour: string;
  mainText: string;
  showButton: boolean;
  buttonLabel: string;
  buttonDestinationUrl: string;
  buttonBackgroundColour: string;
  buttonTextColour: string;
  buttonOpensInNewWindow: boolean;
  isPublished: boolean;
}

const DEFAULT_PROMO_BANNER_CONFIG: PromoBannerConfig = {
  barBackgroundColour: '#2E2C28',
  barTextColour: '#FFFFFF',
  mainText: '',
  showButton: false,
  buttonLabel: '',
  buttonDestinationUrl: '',
  buttonBackgroundColour: '#fff',
  buttonTextColour: '#1A4989',
  buttonOpensInNewWindow: false,
  isPublished: false
};

@Component({
  selector: 'app-webfacing-header-v2',
  templateUrl: './webfacing-header-v2.component.html',
  styleUrls: ['./webfacing-header-v2.component.scss'],
  standalone: true,
  imports: [CommonModule, NgSelectModule]
})
export class WebfacingHeaderV2Component extends AppComponentBase implements OnInit, OnDestroy {
  /**
   * Calculator page title (e.g., "Cable Sizing Calculator")
   * Gets automatically set based on current route if not provided
   */
  public calculatorName: string = '';

  /**
   * Standard variant for Cable Sizing (e.g., "AS/NZS 3008.1.1")
   * Gets automatically set based on current route if not provided
   */
  public standardVariant: string = 'AS/NZS 3008.1.1';

  /**
   * Last selected standard variant URL (persists when switching tabs)
   */
  public lastSelectedStandardVariantUrl: string = '/cable-sizing-as';

  /**
   * Whether to show the Generate Report button
   * Automatically determined based on current route
   */
  public showGenerateReport: boolean = false;

  /**
   * Whether it's a Cable Sizing page (shows standard dropdown)
   * Gets automatically set based on current route
   */
  public isCableSizingPage: boolean = false;

  // Internal state
  public user!: UserLoginInfoDto | undefined;
  public isAdminUser: boolean = false;
  public isAnnualProfessionalPlan: boolean = false;
  public isReportDisabled = true;
  public isGeneratingReport: boolean = false;
  public showPromoBanner: boolean = false;
  public promoBannerConfig: PromoBannerConfig = { ...DEFAULT_PROMO_BANNER_CONFIG };
  public isBannerVisible: boolean = true;
  private sub!: Subscription;
  private isGeneratingReportSub!: Subscription;
  private detectionSub!: Subscription;
  /** Latest CPW-5547 detection, retained so it can be re-applied on every navigation. */
  private latestDetection: RegionalDetectionResult | null = null;
  private lastScrollTop: number = 0;
  private readonly scrollThreshold: number = 40;
  public FeatureCode = FeatureCode;
  public isMobileMenuOpen: boolean = false;
  public isStandardsDropdownOpen: boolean = false;
  public isMobileCalcDropdownOpen: boolean = false;
  public isTabsMenuOpen: boolean = false;
  public isLocalhost: boolean = location.hostname === "localhost";

  // Standard variants for Cable Sizing
  public standardVariants: StandardVariant[] = [
    { name: 'AS/NZS 3008.1.1', code: 'as', url: '/cable-sizing-as', label: 'AS/NZS 3008.1.1 (Australian)' },
    { name: 'BS 7671', code: 'bs', url: '/cable-sizing-bs', label: 'BS 7671 (British)' },
    { name: 'IEC 60364-5-52', code: 'iec', url: '/cable-sizing-iec', label: 'IEC 60364-5-52 (International)' },
    { name: 'AS/NZS 3008 (NZ)', code: 'nz', url: '/cable-sizing-nz', label: 'AS/NZS 3008.1.2 (New Zealand)' },
    { name: 'NFPA 70 (NEC)', code: 'nec', url: '/cable-sizing-nec', label: 'NEC NFPA 70 (United States)' }
  ];

  /**
   * The order standardVariants ships in, captured before CPW-5547's regional reorder can touch it.
   * Never mutated - every reorder is derived from this, so navigating off a Cable Sizing page can
   * restore the default order exactly.
   */
  private readonly defaultStandardOrder: ReadonlyArray<StandardVariant> = [...this.standardVariants];

  // Calculator tabs for Row 2
  public calculatorTabs: CalculatorTab[] = [
    { id: 'cable-sizing', label: 'Cable Sizing', icon: 'flaticon-cable', svgPath: 'assets/custom/img/cable-sizing.svg', url: '/cable-sizing-as', isActive: false },
    { id: 'max-demand', label: 'Max Demand', icon: 'flaticon-chart', svgPath: 'assets/custom/img/max-demand.svg', url: '/max-demand', isActive: false },
    { id: 'cable-pulling', label: 'Cable Pulling', icon: 'flaticon-pull', svgPath: 'assets/custom/img/cable-pulling.svg', url: '/cable-pulling', isActive: false },
    { id: 'arc-flash', label: 'Arc Flash', icon: 'flaticon-warning', svgPath: 'assets/custom/img/arc-flash.svg', url: '/arc-flash', isActive: false },
    { id: 'protection-coordination', label: 'Protection Coordination', icon: 'flaticon-shield', svgPath: 'assets/custom/img/protection-coordination.svg', url: '/protection-coordination', isActive: false }
  ];

  // All available calculators for navigation
  public calculators: CalculatorNavItem[] = [
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing AS/NZS 3008.1.1", url: 'cable-sizing-as' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing BS 7671", url: 'cable-sizing-bs' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing AS/NZS 3008.1.2", url: 'cable-sizing-nz' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing IEC 60364-5-52", url: 'cable-sizing-iec' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing NEC NFPA 70", url: 'cable-sizing-nec' },
    { "name": "Maximum Demand Calculator to AS/NZS 3000", "sortName": "Maximum Demand to AS/NZS 3000", url: 'max-demand' },
    { "name": "Protection Coordination Calculator", "sortName": "Protection Coordination", url: 'protection-coordination' },
    { "name": "Cable Pulling Tensions Calculator", "sortName": "Cable Pulling Tensions", url: 'cable-pulling' },
    { "name": "Arc Flash Calculator to IEEE 1584, ENA, DGUV, and DC methods", "sortName": "Arc Flash", url: 'arc-flash' },
    // { "name": "LV Network Calculator", url: 'https://cablepro.elek.com/create-free-account', external: true },
  ];

  public calculatorMappings = [
    { path: '/cable-sizing-as', id: 2 },
    { path: '/cable-sizing-nz', id: 3 },
    { path: '/cable-sizing-bs', id: 4 },
    { path: '/cable-sizing-iec', id: 5 },
    { path: '/cable-sizing-nec', id: 13 },
    { path: '/max-demand', id: 6 },
    { path: '/protection-coordination', id: 8 },
    { path: '/cable-pulling', id: 9 },
    { path: '/arc-flash', id: 10 }
  ];

  public _router: Router;
  redirectUrl: string = this.isLocalhost ? '/' : '/calculators/';
  showReportButton: boolean = false;

  // Storage keys for localStorage persistence
  private readonly STORAGE_KEY_STANDARD = 'cablepro_selected_standard';
  private readonly STORAGE_KEY_STANDARD_URL = 'cablepro_selected_standard_url';

  constructor(
    injector: Injector,
    private sharedService: SharedService,
    private _appSessionService: AppSessionService,
    public loginService: LoginService,
    private loaderService: LoaderService,
    private _authService: AppAuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private modalService: NgbModal,
    private readonly _regionalStandardService: RegionalStandardService
  ) {
    super(injector);
    (window as any).wf_header_v2 = this;

    // Load saved values from localStorage
    this.loadFromLocalStorage();

    this.user = this._appSessionService.user;
    if (this.user) {
      this.isAdminUser = (this.user.id === 1 || this.user.id === 2);
    }
    console.log('WebfacingHeaderV2Component - User Info:', this.user);
    if (this.user?.premiumUser) {
      let IsUserHasProfessionalPlan = this.user?.userStripePlanLevel == 3;
      this.isAnnualProfessionalPlan = IsUserHasProfessionalPlan && this.user?.userStripePlanInterval?.toLowerCase().trim() === 'year';
    }
    this._router = router;

    // Initialize header properties based on current route
    this.initializeHeaderPropertiesFromRoute(router.url);
  }

  private loadPromoBannerConfiguration(): void {
    const raw = abp.setting.get('App.UiManagement.Header.PromoBannerConfiguration');
    let parsed: Partial<PromoBannerConfig> = {};

    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = {};
      }
    }

    this.promoBannerConfig = {
      ...DEFAULT_PROMO_BANNER_CONFIG,
      ...parsed,
      barTextColour: parsed.barTextColour || DEFAULT_PROMO_BANNER_CONFIG.barTextColour,
      buttonTextColour: parsed.buttonTextColour || DEFAULT_PROMO_BANNER_CONFIG.buttonTextColour
    };
    this.showPromoBanner = this.promoBannerConfig.isPublished === true;
  }

  ngOnInit() {
    this.updateActiveTab(this.router.url);

    this.showReportButton = this.isReportButtonDisplay(this.router.url);
    this.openRegistrationModalIfRequested(this.router.url);
    this.loadPromoBannerConfiguration();
    this.isBannerVisible = true;
    this.onWindowScroll();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.showReportButton = this.isReportButtonDisplay(event.url);
        this.updateActiveTab(event.url);
        this.initializeHeaderPropertiesFromRoute(event.url);
        this.openRegistrationModalIfRequested(event.url);
        this.closeAllMenus();

        // Re-evaluate on every navigation, not just when detection lands: the reorder is
        // Cable-Sizing-only and must be undone when the user moves to another calculator.
        if (this.latestDetection) {
          this.applyRegionalDetection(this.latestDetection, event.url);
        }
      });

    // CPW-5547. Detection is kicked off by WebfacingComponent; this only consumes the result.
    // ReplaySubject(1) means it does not matter whether detection settles before or after this
    // component is constructed.
    this.detectionSub = this._regionalStandardService.detection$.subscribe(result => {
      this.latestDetection = result;
      this.applyRegionalDetection(result, this.router.url);
    });

    this.sub = this.loaderService.reportDisabled$
      .subscribe((disabled: boolean) => {
        this.isReportDisabled = disabled;
      });

    this.isGeneratingReportSub = this.loaderService.isGeneratingReport$
      .subscribe((isGenerating: boolean) => {
        this.isGeneratingReport = isGenerating;
      });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.isGeneratingReportSub) {
      this.isGeneratingReportSub.unsubscribe();
    }
    if (this.detectionSub) {
      this.detectionSub.unsubscribe();
    }
  }

  /**
   * CPW-5547: applies the detected regional standard to the header.
   *
   * Runs on every navigation as well as when detection settles, because both the reorder and the
   * popup are scoped to Cable Sizing pages and must be withdrawn elsewhere.
   */
  private applyRegionalDetection(result: RegionalDetectionResult, url: string): void {
    const onCableSizing = this.isCableSizingRoute(url);

    this.applyDetectedStandardOrder(
      result.status === 'detected' && onCableSizing ? result.standardCode : null);

    // Every condition below must hold for the popup to appear.
    if (result.status !== 'detected') {
      return; // unmapped country or failed lookup - site default, nothing to recommend
    }
    if (!onCableSizing) {
      return; // silent on Max Demand / Cable Pulling / Arc Flash / Protection Coordination / Index
    }

    // Derive the current standard from the URL rather than from this.standardVariant, which can
    // still hold a stale label restored from localStorage in the constructor.
    const currentCode = this.standardVariants.find(variant => url.includes(variant.url))?.code;
    if (!currentCode || currentCode === result.standardCode) {
      return; // already viewing the recommended standard
    }
    if (this._regionalStandardService.hasPopupBeenShown()) {
      return; // once per session, whether it was actioned or dismissed
    }
    if (this.modalService.hasOpenModals()) {
      return; // never stack on the login/register modal - both use backdrop: 'static'
    }

    const target = this.standardVariants.find(variant => variant.code === result.standardCode);
    if (!target) {
      return;
    }

    // Marked on open, not on close: "shown or dismissed" must also cover a user who navigates
    // away while the modal is still up.
    this._regionalStandardService.markPopupShown();

    const modalRef = this.modalService.open(RegionalStandardModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      animation: true,
      windowClass: 'regional-standard-modal-window',
      backdropClass: 'regional-standard-modal-backdrop'
    });
    modalRef.componentInstance.detectedCountryName = result.countryName;
    modalRef.componentInstance.detectedCountryCode = result.countryCode;
    modalRef.componentInstance.detectedShortName = STANDARD_DISPLAY_NAME[result.standardCode];
    modalRef.componentInstance.currentShortName = STANDARD_DISPLAY_NAME[currentCode as StandardCode];

    modalRef.result.then(
      outcome => {
        // Delegate to the existing handler so the switch reuses its localhost/prod navigation
        // branch and localStorage persistence.
        if (outcome === 'switch') {
          this.onStandardChange(target);
        }
      },
      () => { /* dismissed - stay on the current standard */ });
  }

  /**
   * Moves the detected standard to the top of the dropdown, or restores the shipped order when
   * passed null. Always assigns a new array rather than mutating in place.
   */
  private applyDetectedStandardOrder(code: StandardCode | null): void {
    const base = this.defaultStandardOrder;
    const detected = code ? base.find(variant => variant.code === code) : undefined;

    this.standardVariants = detected
      ? [detected, ...base.filter(variant => variant.code !== code)]
      : [...base];
  }

  /**
   * Load standard variant and URL from localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const savedStandard = localStorage.getItem(this.STORAGE_KEY_STANDARD);
      const savedUrl = localStorage.getItem(this.STORAGE_KEY_STANDARD_URL);

      if (savedStandard) {
        this.standardVariant = savedStandard;
      }
      if (savedUrl) {
        this.lastSelectedStandardVariantUrl = savedUrl;
      }
    } catch (e) {
      console.warn('Failed to load from localStorage:', e);
    }
  }

  /**
   * Save standard variant and URL to localStorage
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_STANDARD, this.standardVariant);
      localStorage.setItem(this.STORAGE_KEY_STANDARD_URL, this.lastSelectedStandardVariantUrl);
    } catch (e) {
      console.warn('Failed to save to localStorage:', e);
    }
  }

  /**
   * Initialize calculator name, standard variant, and show report flag based on current route
   */
  private initializeHeaderPropertiesFromRoute(url: string): void {
    // Reset calculator name, keep standard variant default
    this.calculatorName = '';
    this.isCableSizingPage = false;
    this.showGenerateReport = false;

    this.setCalculatorNameFromRoute(url);
    // Determine if this is a cable sizing page and which standard
    this.isCableSizingPage = this.isCableSizingRoute(url);
    if (this.isCableSizingPage) {
      this.setCalculatorNameAndStandardFromRoute(url);
      // Show report dropdown only for cable sizing (not simple button)
      this.showGenerateReport = true;
    } else {
      // Set calculator name from other calculators
      // Reset to default standard variant when leaving cable sizing
      // this.standardVariant = 'AS/NZS 3008.1.1';
      // Show report button for non-cable-sizing calculators (not dropdown)
      this.showGenerateReport = !this.isHomeOrInvalidRoute(url);
    }
  }

  /**
   * Determine if current route is a cable sizing page
   */
  private isCableSizingRoute(url: string): boolean {
    return url.includes('/cable-sizing-as') ||
      url.includes('/cable-sizing-bs') ||
      url.includes('/cable-sizing-nz') ||
      url.includes('/cable-sizing-iec') ||
      url.includes('/cable-sizing-nec');
  }

  /**
   * Check if route is home or invalid (no calculator selected)
   */
  private isHomeOrInvalidRoute(url: string): boolean {
    return url === '/' || url === '' || !this.getCalculatorFromRoute(url);
  }

  /**
   * Get calculator object from route URL
   */
  private getCalculatorFromRoute(url: string): CalculatorNavItem | undefined {
    return this.calculators.find(calc =>
      calc.url && url.includes(calc.url)
    );
  }

  /**
   * Set calculator name and standard variant from cable sizing route
   */
  private setCalculatorNameAndStandardFromRoute(url: string): void {

    let currentStandard = this.standardVariants.find(variant => url.includes(variant.url));
    if (currentStandard) {
      this.standardVariant = currentStandard.label;
      this.lastSelectedStandardVariantUrl = currentStandard.url;
    }
    // Save to localStorage
    this.saveToLocalStorage();
  }

  /**
   * Set calculator name from other calculator routes
   */
  private setCalculatorNameFromRoute(url: string): void {
    const calculator = this.getCalculatorFromRoute(url);
    if (calculator) {
      this.calculatorName = calculator.name || '';
    }
    else if (url === '/' || url === '') {
      this.calculatorName = '';
    }
  }

  /**
   * Update active tab based on current route
   */
  private updateActiveTab(url: string): void {
    this.calculatorTabs.forEach(tab => {
      tab.isActive = url.includes(tab.id) || (tab.id === 'cable-sizing' && url.includes('cable-sizing'));
    });
  }

  /**
   * Navigate to calculator tab
   * For Cable Sizing, navigate to the last selected standard variant
   */
  onTabChange(tab: CalculatorTab): void {
    // Close all menus
    this.closeAllMenus();

    // If navigating to Cable Sizing, use the last selected standard variant URL
    let urlToNavigate = tab.url;
    if (tab.id === 'cable-sizing') {
      urlToNavigate = this.lastSelectedStandardVariantUrl;
    }
    if (this.isLocalhost) {
      this.router.navigate([urlToNavigate]);
    }
    else {
      location.href = `/calculators${urlToNavigate}`;
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const currentScrollTop = window.scrollY || document.documentElement.scrollTop || 0;

    if (currentScrollTop <= this.scrollThreshold) {
      this.isBannerVisible = true;
    } else if (currentScrollTop > this.scrollThreshold) {
      this.isBannerVisible = false;
    }
    // } else if (currentScrollTop < this.lastScrollTop - this.scrollThreshold) {
    //   this.isBannerVisible = true;
    // }

    this.lastScrollTop = currentScrollTop;
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.isMobileCalcDropdownOpen = false;
    this.isStandardsDropdownOpen = false;
    this.isTabsMenuOpen = false;
  }

  /**
   * Toggle tabs navigation menu (Bootstrap navbar)
   */
  toggleTabsMenu(): void {
    this.isTabsMenuOpen = !this.isTabsMenuOpen;
  }

  /**
   * Toggle mobile calculator dropdown
   */
  toggleMobileCalcDropdown(): void {
    this.isMobileCalcDropdownOpen = !this.isMobileCalcDropdownOpen;
    this.isMobileMenuOpen = false;
    this.isStandardsDropdownOpen = false;
    this.isTabsMenuOpen = false;
  }

  /**
   * Toggle standards dropdown
   */
  toggleStandardsDropdown(): void {
    console.log('Toggling standards dropdown. Current state:', this.isStandardsDropdownOpen);
    this.isStandardsDropdownOpen = !this.isStandardsDropdownOpen;
    console.log('Toggling standards dropdown. Current state:', this.isStandardsDropdownOpen);

  }

  /**
   * Close all menus
   */
  closeAllMenus(): void {
    this.isMobileMenuOpen = false;
    this.isMobileCalcDropdownOpen = false;
    this.isStandardsDropdownOpen = false;
    this.isTabsMenuOpen = false;
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  /**
   * Close mobile calculator dropdown
   */
  closeMobileCalcDropdown(): void {
    this.isMobileCalcDropdownOpen = false;
  }

  /**
   * Get the label of the active calculator tab
   * Used for mobile navbar toggler display
   */
  getActiveTab() {
    let tab = this.calculatorTabs.find(tab => tab.isActive);
    return tab;
    // return activeTab ? activeTab.label : (this.calculatorTabs[0]?.label || 'Calculators');
  }

  /**
   * Get truncated mobile title
   * Removes "Calculator" word and truncates version numbers
   * Examples: "Cable Sizing Calculator AS/NZS 3008.1.1" → "Cable Sizing · AS/NZS"
   */
  getMobileTruncatedTitle(): string {
    let title = this.calculatorName.replace(' Calculator', '').trim();

    if (this.isCableSizingPage && this.standardVariant) {
      // Extract just the standard code (e.g., "AS/NZS" from "AS/NZS 3008.1.1")
      const standardCode = this.standardVariant.split(' ')[0];
      return `${title} · ${standardCode}`;
    }

    return title;
  }

  /**
   * Close standards dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector('.item.cable-sizing');

    // Close dropdown if click is outside and dropdown is open
    if (dropdown && !dropdown.contains(target) && this.isStandardsDropdownOpen) {
      this.isStandardsDropdownOpen = false;
    }
  }

  /**
   * Handle escape key to close menus
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isMobileMenuOpen || this.isMobileCalcDropdownOpen) {
      event.preventDefault();
      this.closeAllMenus();
    }
  }

  /**
   * Open registration modal when on home route and query param openRegistration=true
   */
  private openRegistrationModalIfRequested(url: string): void {
    const urlWithoutQuery = url.split('?')[0] || '/';
    const openRegistrationParam = this.route.snapshot.queryParamMap.get('openRegistration');

    if ((urlWithoutQuery === '/' || urlWithoutQuery === '') && openRegistrationParam === 'true') {
      this.onRegister();
    }
  }

  /**
   * Handle standard variant selection (Cable Sizing only)
   * Saves the selected variant and navigates to it
   */
  onStandardChange(variant: StandardVariant): void {
    if (variant.url) {
      console.log('Standard change requested:', variant);
      // Close all menus
      this.closeAllMenus();

      // Save the selected standard variant for later use
      this.lastSelectedStandardVariantUrl = variant.url;
      this.standardVariant = variant.label;

      // Persist to localStorage before navigation
      this.saveToLocalStorage();

      // window.location.href = variant.url;

      if (this.isLocalhost) {
        this.router.navigate([variant.url]);
      }
      else {
        location.href = `/calculators${variant.url}`;
      }
    }
  }

  /**
   * Handle calculator change from dropdown
   */
  // onCalculationChange(calculator: CalculatorNavItem): void {
  //   if (calculator.disabled || !calculator.url) {
  //     return;
  //   }

  //   if (calculator.external) {
  //     window.location.href = calculator.url;
  //   } else {
  //     this.router.navigate([calculator.url]);
  //   }
  // }

  /**
   * Generate report button click
   */
  // @RequireLogin()
  onReportClick(): void {
    this.closeMobileMenu();
    this.sharedService.triggerAction('report');
  }

  /**
   * Calculation steps button click
   */
  onCalculationStep(): void {
    if (this._appSessionService.hasPermission(this.FeatureCode.FDCSCSIS)) {
      this.sharedService.triggerAction('calculationstep');
    }
    else {
      if(!this.user || this.user?.isWebFacingUser) {
        this.onRegister();
      }
      else {
        this._appSessionService.showUpgradeAlert();
      }
    }
  }

  /**
   * Navigate to all projects page
   */
  onAllProjectsClick(): void {
    window.location.href = AppConsts.appBaseUrl;
  }

  /**
   * Upgrade plan
   */
  UpgradePlan(): void {
    if (this.isLocalhost) {
      this.router.navigate(['/pricing']);
    }
    else {
      location.href = '/calculators/pricing';
    }
  }

  /**
   * Show login modal
   */
  onLogin(): void {
    this.closeMobileMenu();
    this.modalService.open(WebfacingLoginComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      animation: true
    });
  }

  /**
   * Show register modal
   */
  onRegister(): void {
    this.closeMobileMenu();
    this.modalService.open(WebfacingRegisterComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      animation: true
    });
  }

  /**
   * Logout
   */
  logout(): void {
    this.closeMobileMenu();
    this._authService.logout(true, this.redirectUrl);
  }

  // /**
  //  * Get latest plan name
  //  */
  // getLatestPlanName(planLevel: number): string {
  //   switch (planLevel) {
  //     case 1:
  //       return "ESSENTIAL";
  //     case 2:
  //       return "PREMIUM";
  //     case 3:
  //       return "PROFESSIONAL";
  //     default:
  //       return "";
  //   }
  // }

  /**
   * Get full calculator title with standard variant
   */
  getFullCalculatorTitle(isSort: boolean = false): string {
    if (isSort) {
      const calc = this.getCalculatorFromRoute(this._router.url);
      return calc ? calc.sortName : '';
    }
    if (this.isCableSizingPage && this.standardVariant) {
      return `${this.calculatorName} ${this.standardVariant}`;
    }
    return this.calculatorName;
  }

  isReportButtonDisplay(url: string): boolean {
    const reportUrls = ['/cable-sizing-as', '/cable-sizing-bs', '/cable-sizing-nz', '/cable-sizing-nec', '/cable-pulling', '/arc-flash', '/max-demand', '/protection-coordination', '/cable-sizing-iec'];
    return reportUrls.some(route => url.includes(route));
  }

  redirectToFullAppUrl() {
    let asNzUrl = "https://elek.com/electrical-software/elek-cable-pro-web/";
    let bsUrl = "https://elek.com/electrical-software/elek-cable-pro-web-bs7671/";
    let redirectUrl = "";

    let url = this.router.url;
    if (url.includes('/cable-sizing-as') || url.includes('/cable-sizing-nz')) {
      redirectUrl = asNzUrl;
    } else if (url.includes('/cable-sizing-bs')) {
      redirectUrl = bsUrl;
    } else {
      redirectUrl = asNzUrl;
    }

    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  }

}
